import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import gql from 'graphql-tag';
import get from 'lodash-es/get';
import { tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    GetMenusListAction,
    SelectMenuAction,
    SetActivatedMenusListAction
} from 'src/app/_user/store/menu/menu.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { compare } from 'src/app/utils/array.utils';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import {
    ContextInfosInput,
    MenuDto,
    MenuPageParamDto,
    MutationGetListeMenuGeneriqueArgs
} from 'src/graphql/generated-types';

export class MenuStateModel {

    canDisplayMenus = false;

    menusList: MenuDto[] = [];

    selectedMenu: MenuDto = null;

    activatedMenusList: MenuDto[] = [];

    modulesList: string[] = [];

}

@Injectable()
@State<MenuStateModel>({
    name: 'menu',
    defaults: new MenuStateModel()
})
export class MenuState {

    constructor(private readonly graphqlService: GraphqlService,
                private store: Store) {
    }

    @Selector()
    static menusParams(state: MenuStateModel): MenuPageParamDto[] {
        return get(state.selectedMenu, ['MenuPage', 'ListeParametres', 'MenuPageParamDTO'], []);
    }

    @Selector()
    static activatedMenusList(state: MenuStateModel): MenuDto[] {
        return state.activatedMenusList || [];
    }

    @Selector()
    static selectedMenu(state: MenuStateModel): MenuDto {
        return state.selectedMenu;
    }

    @Selector()
    static canDisplayMenus(state: MenuStateModel): boolean {
        return state.canDisplayMenus;
    }

    @Selector()
    static menusList(state: MenuStateModel): MenuDto[] {
        return state.menusList;
    }

    @Action(SetActivatedMenusListAction)
    setActivatedMenusList(state: StateContext<MenuStateModel>, {activatedMenusList}: SetActivatedMenusListAction) {
        state.patchState({activatedMenusList});
    }

    @Action(GetMenusListAction)
    getMenusList({patchState}: StateContext<MenuStateModel>) {
        const userContext: ContextInfosInput = this.store.selectSnapshot(UserState.userContext);
        const modulesList: string[] = this.store.selectSnapshot(UserState.modulesList);
        const isSuperAdmin = this.store.selectSnapshot(UserState.isSuperAdmin);

        return this.graphqlService.mutateOne<'GetListeMenuGenerique', MutationGetListeMenuGeneriqueArgs>({
            mutation: gql`
                mutation getMenusList($contexte: ContextInfosInput, $criteres: CriteresMenuDtoInput) {
                    GetListeMenuGenerique(contexte: $contexte, criteres: $criteres) {
                        MenuDTO {
                            Activite{
                                Id
                                Libelle
                            }
                            Id
                            Code
                            Libelle
                            MenuPage {
                                Code
                                Classe
                                ListeParametres {
                                    MenuPageParamDTO {
                                        Ordre
                                        Libelle
                                        Id
                                        Valeur
                                    }
                                }
                            }
                            Ordre
                            ParentMenu {
                                Id
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    WithDev: !environment.production,
                    ListeCodeModuleAccessible: {
                        string: modulesList
                    },
                    IsSuperAdmin: isSuperAdmin,
                    WithMenuPageEtMenuPageParam: true,
                    WithParentMenu: true,
                    WithTypeActivite: true,
                    WithActivite: true,
                    PrendreEnCompteProfilUser: true,
                    IdUserPourProfil: userContext.CurrentUserId,
                    IdCe: userContext.CurrentCE,
                    IdDelegPourFiltreActivite: userContext.CurrentCE,
                    CodeVersionLogiciel: constants.codeVersionLogiciel,
                    WithParentMenuFavori: true,
                    WithUser: true,
                    WithCeDelegation: true,
                }
            }
        })
        .pipe(
            tap((GetListeMenuGenerique) => {
                patchState({
                    menusList: (GetListeMenuGenerique?.MenuDTO || []).sort(compare('Ordre')),
                    canDisplayMenus: true
                });
            }),
        );
    }

    @Action(SelectMenuAction)
    selectMenu({patchState, getState}: StateContext<MenuStateModel>, {menu}: SelectMenuAction) {
        patchState({selectedMenu: menu});
    }

}
